"use client"

import FeaturedPost from "@/(admin)/modules/ui-builder/config/components/FeaturedPost"
import { CategoryBannerSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/CategoryBannerSkeleton"
import PostLink from "@/components/PostLink"
import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import {
  buildTamdaMediaCategoryBannerBannersKey,
  buildTamdaMediaCategoryBannerPostsKey,
} from "@/lib/extract-batch-requests"
import { toImageSrc } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { twMerge } from "tailwind-merge"
import { CategoryBannerLayoutProps } from "."
import { BannerData, PostData } from "../../../../../../../types/types"
import BannerCard from "../../../components/BannerCard"
import { generateResponsiveCSS } from "../../../lib/helper"
import { getPostFields, pickPostImageFormat } from "../../../lib/post-helpers"
import useIsMobile from "../../../lib/use-is-mobile"
import { useStableComponentId } from "../TamdaMediaNewsLayout/hooks/useStableComponentId"

export const RenderConfig: ComponentConfig<CategoryBannerLayoutProps> = {
  render: ({ categoryId, topBannerId, bottomBannerId, postsPerPage, id: componentId }) => {
    const id = useStableComponentId(componentId)
    const responsiveCSS = generateResponsiveCSS(id, {}, "desktop")
    const isMobile = useIsMobile()
    const batchData = useBatchDataMap()
    const postsEntry = resolveBatchDataEntry<{ data?: PostData[] }>(batchData, {
      key: buildTamdaMediaCategoryBannerPostsKey(id),
      prefix: "tamda-media-category-banner-layout:",
      suffix: ":posts",
    })
    const bannersEntry = resolveBatchDataEntry<{ data?: BannerData[] }>(batchData, {
      key: buildTamdaMediaCategoryBannerBannersKey(id),
      prefix: "tamda-media-category-banner-layout:",
      suffix: ":banners",
    })
    const posts = Array.isArray(postsEntry?.data) ? postsEntry.data : []
    const banners = Array.isArray(bannersEntry?.data) ? bannersEntry.data : []
    const loading = (categoryId || topBannerId || bottomBannerId) && posts.length === 0 && banners.length === 0
    const getBannerById = (bannerId: string) => banners.find(b => b.id.toString() === bannerId.toString())
    const topBanner = topBannerId ? getBannerById(topBannerId) : undefined
    const bottomBanner = bottomBannerId ? getBannerById(bottomBannerId) : undefined

    const renderPost = (post: PostData) => {
      const { postTitle, description, postSlug, formattedPublishedAt } = getPostFields(post)
      return (
        <PostLink
          href={`/${postSlug}`}
          postId={post.id}
          className="flex sm:flex-col flex-row sm:gap-2.5 gap-2 image-hover group"
          key={post.id}
        >
          <div className="sm:h-[163px] h-[70px] sm:w-full w-[42.8%] image-hover group">
            <Image
              src={toImageSrc(pickPostImageFormat(post, "small") ?? undefined)}
              alt={postTitle}
              width={300}
              height={163}
              sizes="(max-width: 640px) 43vw, (max-width: 1024px) 33vw, 295px"
              className="w-full h-full object-cover"
              loading="lazy"
              quality={75}
            />
          </div>
          <div className="flex flex-col gap-[6px] sm:w-full w-[57.2%]">
            {/* Không bọc PostLink ở đây: cả card đã nằm trong PostLink phía ngoài.
                Lồng <a> trong <a> gây hydration error "cannot be a descendant of <a>". */}
            <h3 className="sm:text-lg sm:leading-[22.4px] text-base leading-[22.4px] sm:font-bold font-normal">
              {postTitle}
            </h3>
            <div className="sm:block hidden">
              {description && <p className="text-base text-[#4F4F4F] line-clamp-3">{description}</p>}
            </div>
            <p className="sm:block hidden text-sm font-normal leading-[21.7px] text-black group-hover:text-black">
              {formattedPublishedAt}
            </p>
          </div>
        </PostLink>
      )
    }

    const renderMobileFirstPost = (post: PostData) => {
      const { postTitle, postSlug } = getPostFields(post)
      return (
        <PostLink
          key={post.id}
          href={`/${postSlug}`}
          postId={post.id}
          locale={post.attributes.locale}
          className="flex flex-col gap-2 image-hover group"
        >
          <div className="h-[192px]">
            <Image
              src={toImageSrc(pickPostImageFormat(post, "small") ?? undefined)}
              alt={postTitle}
              width={300}
              height={192}
              sizes="100vw"
              className="w-full h-full object-cover"
              loading="lazy"
              quality={75}
            />
          </div>
          <div className="flex flex-col gap-[6px]">
            <h3 className="text-base leading-[22.4px] font-normal">{postTitle}</h3>
          </div>
        </PostLink>
      )
    }

    const MobileLayout = () => {
      const firstPost = posts[0]
      const nextThree = posts.slice(1, 4)
      const rest = posts.slice(4)
      return (
        <div>
          <div className="sm:pb-0 pb-10">{firstPost && renderMobileFirstPost(firstPost)}</div>
          <BannerCard banner={topBanner} isMobile={isMobile} />
          <div className="grid grid-cols-1 gap-[26px] sm:pt-0 sm:pb-0 pt-[13px] pb-[26px]">
            {nextThree.map(renderPost)}
          </div>
          <BannerCard banner={bottomBanner} isMobile={isMobile} />
          {rest.length > 0 && <div className="grid grid-cols-1 gap-6">{rest.map(renderPost)}</div>}
        </div>
      )
    }

    return (
      <>
        {responsiveCSS}
        <div id={id} className={`${twMerge(id)} container mx-auto max-w-[1160px] px-4`}>
          {loading ? (
            <CategoryBannerSkeleton />
          ) : (
            <div className="flex gap-4 w-full">
              <div className="w-full sm:w-[75.8%] flex flex-col gap-4">
                {posts.length === 0 ? (
                  <div className="text-center py-20 text-gray-500">No posts found.</div>
                ) : isMobile ? (
                  <MobileLayout />
                ) : (
                  <>
                    {posts[0] && <FeaturedPost post={posts[0]} imageClassName="sm:h-[320px]" />}
                    {posts.length > 1 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                        {posts.slice(1).map(renderPost)}
                      </div>
                    )}
                  </>
                )}
              </div>
              {!isMobile && (
                <div className="w-[24.2%] flex flex-col gap-5">
                  <BannerCard banner={topBanner} isMobile={isMobile} />
                  <BannerCard banner={bottomBanner} isMobile={isMobile} />
                </div>
              )}
            </div>
          )}
        </div>
      </>
    )
  },
}
